import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import StudentLayout from '@/components/layouts/StudentLayout';
import { 
  Trophy, Star, Clock, CheckCircle, Target, Zap, Flame,
  BookOpen, Gamepad2, PenTool, FileText, Award, Sparkles,
  TrendingUp, ArrowLeft, RefreshCw
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';

const WeeklyChallengesPage = () => {
  const navigate = useNavigate();
  const { user, api } = useAuth();
  const [challenges, setChallenges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [completedCount, setCompletedCount] = useState(0);

  const iconMap = {
    BookOpen: BookOpen,
    Target: Target,
    Award: Award,
    Star: Star,
    Gamepad2: Gamepad2,
    Trophy: Trophy,
    TrendingUp: TrendingUp,
    PenTool: PenTool,
    FileText: FileText,
    Flame: Flame,
    Sparkles: Sparkles,
    Zap: Zap
  };

  useEffect(() => {
    fetchChallenges();
  }, []);

  useEffect(() => {
    if (timeRemaining > 0) {
      const timer = setInterval(() => {
        setTimeRemaining(t => Math.max(0, t - 1));
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [timeRemaining]);

  const fetchChallenges = async () => {
    try {
      const response = await api.get('/challenges/weekly');
      setChallenges(response.data.challenges);
      setTimeRemaining(response.data.time_remaining_seconds);
      setCompletedCount(response.data.completed_count);
    } catch (error) {
      console.error('Error fetching challenges:', error);
      toast.error('Failed to load challenges');
    } finally {
      setLoading(false);
    }
  };

  const refreshProgress = async () => {
    setRefreshing(true);
    try {
      await api.post('/challenges/refresh');
      await fetchChallenges();
      toast.success('Progress updated!');
    } catch (error) {
      console.error('Error refreshing:', error);
    } finally {
      setRefreshing(false);
    }
  };

  const formatTimeRemaining = (seconds) => {
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    
    if (days > 0) return `${days}d ${hours}h left`;
    if (hours > 0) return `${hours}h ${mins}m left`;
    return `${mins}m left`;
  };

  const totalPoints = challenges.reduce((sum, c) => sum + c.points, 0);
  const earnedPoints = challenges.filter(c => c.completed).reduce((sum, c) => sum + c.points, 0);

  return (
    <StudentLayout>
      <div data-testid="weekly-challenges-page" className="max-w-4xl mx-auto">
        {/* Back Button */}
        <button 
          onClick={() => navigate('/student')}
          className="flex items-center gap-2 text-slate-600 hover:text-purple-600 font-medium mb-6"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to Dashboard
        </button>

        {/* Header */}
        <div className="student-card bg-gradient-to-r from-purple-500 to-pink-500 text-white mb-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
                <Target className="w-10 h-10 text-white" />
              </div>
              <div>
                <h1 className="font-heading text-3xl font-bold">Weekly Challenges</h1>
                <p className="text-purple-100">Complete challenges to earn bonus points!</p>
              </div>
            </div>
            
            <div className="flex items-center gap-6">
              <div className="text-center">
                <div className="flex items-center gap-2 mb-1">
                  <Clock className="w-5 h-5 text-purple-200" />
                  <span className="text-xl font-bold">{formatTimeRemaining(timeRemaining)}</span>
                </div>
                <p className="text-xs text-purple-200">Until Reset</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold">{completedCount}/{challenges.length}</p>
                <p className="text-xs text-purple-200">Completed</p>
              </div>
            </div>
          </div>
        </div>

        {/* Progress Overview */}
        <div className="student-card mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="font-heading text-xl font-bold text-slate-800">This Week's Progress</h2>
              <p className="text-slate-500 text-sm">Earn up to {totalPoints} bonus points</p>
            </div>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={refreshProgress}
              disabled={refreshing}
              className="gap-2"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
          
          <div className="flex items-center gap-4 mb-2">
            <Progress value={(completedCount / challenges.length) * 100} className="flex-1" />
            <span className="font-bold text-purple-600">{earnedPoints}/{totalPoints} pts</span>
          </div>
        </div>

        {/* Challenges Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-4 border-purple-600 border-t-transparent"></div>
          </div>
        ) : (
          <div className="space-y-4">
            {challenges.map((challenge, index) => {
              const IconComponent = iconMap[challenge.icon] || Target;
              const progressPercent = Math.min(100, (challenge.progress / challenge.target) * 100);
              
              return (
                <div 
                  key={challenge.id}
                  className={`student-card transition-all ${
                    challenge.completed 
                      ? 'bg-emerald-50 border-emerald-300' 
                      : 'hover:shadow-lg'
                  }`}
                  data-testid={`challenge-${index}`}
                >
                  <div className="flex items-center gap-4">
                    {/* Icon */}
                    <div className={`w-14 h-14 rounded-2xl border-2 border-gray-800 flex items-center justify-center flex-shrink-0 ${
                      challenge.completed 
                        ? 'bg-emerald-500' 
                        : 'bg-purple-100'
                    }`}>
                      {challenge.completed ? (
                        <CheckCircle className="w-8 h-8 text-white" />
                      ) : (
                        <IconComponent className="w-7 h-7 text-purple-600" />
                      )}
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-heading font-bold text-slate-800">{challenge.name}</h3>
                        {challenge.completed && (
                          <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs font-bold rounded-full">
                            DONE!
                          </span>
                        )}
                      </div>
                      <p className="text-slate-600 text-sm mb-2">{challenge.description}</p>
                      
                      {/* Progress Bar */}
                      <div className="flex items-center gap-3">
                        <div className="flex-1 bg-slate-200 rounded-full h-2 overflow-hidden">
                          <div 
                            className={`h-full transition-all duration-500 ${
                              challenge.completed ? 'bg-emerald-500' : 'bg-purple-500'
                            }`}
                            style={{ width: `${progressPercent}%` }}
                          />
                        </div>
                        <span className="text-sm font-medium text-slate-600 whitespace-nowrap">
                          {challenge.progress} / {challenge.target}
                        </span>
                      </div>
                    </div>
                    
                    {/* Points */}
                    <div className={`text-center px-4 py-2 rounded-xl ${
                      challenge.completed 
                        ? 'bg-emerald-100' 
                        : 'bg-amber-100'
                    }`}>
                      <div className="flex items-center gap-1">
                        <Star className={`w-5 h-5 ${challenge.completed ? 'text-emerald-600' : 'text-amber-500'}`} />
                        <span className={`font-heading text-xl font-bold ${
                          challenge.completed ? 'text-emerald-600' : 'text-amber-600'
                        }`}>
                          {challenge.points}
                        </span>
                      </div>
                      <p className="text-xs text-slate-500">points</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Tips */}
        <div className="mt-8 student-card bg-amber-50 border-amber-200">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-amber-400 rounded-full flex items-center justify-center flex-shrink-0">
              <Zap className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="font-heading font-bold text-slate-800 mb-1">Pro Tips</h3>
              <ul className="text-sm text-slate-600 space-y-1">
                <li>• Challenges are personalised based on what you've been learning</li>
                <li>• New challenges appear every Monday</li>
                <li>• Complete all challenges for maximum bonus points!</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </StudentLayout>
  );
};

export default WeeklyChallengesPage;
